// Native-token sentinels used across the codebase. Four values mean "native
// chain coin" depending on the data source:
// - the zero-address is common on Relay/0x/Uniswap-V4
// - 0xeeee… is the ERC-20-style sentinel used by 1inch and similar
// - the literal "native" string shows up in some wallet-balance feeds
// - So11…112 is the Wrapped-SOL mint used to represent native SOL on Solana
export const NATIVE_TOKEN_LITERAL = "native";

const WRAPPED_SOL_MINT = "So11111111111111111111111111111111111111112";

export const NATIVE_TOKEN_ADDRESSES: ReadonlySet<string> = new Set([
  NATIVE_TOKEN_LITERAL,
  "0x0000000000000000000000000000000000000000",
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
  WRAPPED_SOL_MINT,
]);

/**
 * Matches any native-token sentinel. Accepts `unknown` for ergonomics — returns
 * `false` for anything that isn't a string.
 *
 * EVM addresses are lowercased before comparison (handles EIP-55 mixed case).
 * Solana base58 mints are case-sensitive, matched exactly.
 */
// Returns `boolean` (not a type predicate) on purpose: a `value is string`
// predicate would narrow the else branch to `never` at call sites where the
// input is already typed `string`, breaking downstream `.toLowerCase()` / etc.
export function isNativeTokenAddress(value: unknown): boolean {
  if (typeof value !== "string" || value.length === 0) return false;
  // Exact match first — handles "native" + Solana base58 (case-sensitive).
  if (NATIVE_TOKEN_ADDRESSES.has(value)) return true;
  // EVM addresses may come mixed-case (EIP-55 or all-upper); normalize.
  const lower = value.toLowerCase();
  return lower.startsWith("0x") && NATIVE_TOKEN_ADDRESSES.has(lower);
}

/**
 * Case-insensitive address equality that treats any pair of native-token
 * sentinels as equal. Useful when comparing an action-definition address
 * (typically 0x0) against a balance-feed address (typically "native").
 */
export function addressesMatch(a: string, b: string): boolean {
  if (isNativeTokenAddress(a) && isNativeTokenAddress(b)) return true;
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * OR's the `metadata.isNative` flag with the sentinel-address check, because
 * some data sources (notably Relay) return the native coin with `isNative:
 * false` alongside a zero-address. Callers should prefer this helper over
 * checking `metadata.isNative` directly.
 */
export function isNativeToken(token: { address: string; metadata: { isNative: boolean } }): boolean {
  return token.metadata.isNative || isNativeTokenAddress(token.address);
}
